import React, { useState } from 'react';
import { X, Upload, AlertCircle, CheckCircle, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import api from '../lib/api';

interface DistribuicaoRow {
  boleto: string;
  usuario: string;
  id: number;
}

interface PreviewData {
  total: number;
  amostra: DistribuicaoRow[];
  validos: number;
  invalidos: number;
  erros: string[];
}

interface ImportDistribuicaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  loteId: number;
  onSuccess: () => void;
}

const ImportDistribuicaoModal: React.FC<ImportDistribuicaoModalProps> = ({
  isOpen,
  onClose,
  loteId,
  onSuccess
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [existingDistribution, setExistingDistribution] = useState<any[] | null>(null);
  const [hasExistingDistribution, setHasExistingDistribution] = useState(false);

  // Carregar distribuição existente quando modal abrir
  React.useEffect(() => {
    if (isOpen && loteId) {
      loadExistingDistribution();
    }
  }, [isOpen, loteId]);

  const loadExistingDistribution = async () => {
    try {
      const response = await api.get(`/disparos/lotes/${loteId}/distribuicao`);
      if (response.success && response.distribuicoes.length > 0) {
        setExistingDistribution(response.distribuicoes);
        setHasExistingDistribution(true);
      } else {
        setExistingDistribution(null);
        setHasExistingDistribution(false);
      }
    } catch (error) {
      console.error('Erro ao carregar distribuição existente:', error);
      setExistingDistribution(null);
      setHasExistingDistribution(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    // Validar tipo de arquivo
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];

    if (!validTypes.includes(selectedFile.type)) {
      setError('Tipo de arquivo inválido. Use .xlsx ou .xls');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setPreview(null);
    setSuccess(false);

    // Fazer preview automático
    await handlePreview(selectedFile);
  };

  const handlePreview = async (fileToPreview: File) => {
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', fileToPreview);

      const response: any = await api.post(
        `/disparos/lotes/${loteId}/distribuicao/preview`,
        formData
        // Não definir Content-Type manualmente - deixar o axios/fetch definir automaticamente com o boundary
      );

      if (response.success) {
        setPreview(response.preview);
      } else {
        setError(response.error || 'Erro ao processar arquivo');
      }
    } catch (err: any) {
      console.error('Erro ao fazer preview:', err);
      setError(
        err.message || 'Erro ao processar arquivo'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!file) {
      setError('Nenhum arquivo selecionado');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response: any = await api.post(
        `/disparos/lotes/${loteId}/distribuicao`,
        formData
        // Não definir Content-Type manualmente - deixar o axios/fetch definir automaticamente com o boundary
      );

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      } else {
        setError(response.error || 'Erro ao salvar distribuição');
      }
    } catch (err: any) {
      console.error('Erro ao salvar distribuição:', err);
      setError(
        err.message || 'Erro ao salvar distribuição'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    setSuccess(false);
    setExistingDistribution(null);
    setHasExistingDistribution(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
              <Upload className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold">Importar Distribuição</DialogTitle>
              <DialogDescription className="text-sm text-gray-600">
                Upload de planilha com atribuição de responsáveis
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Mostrar distribuição existente ou instruções */}
          {hasExistingDistribution ? (
            <div className="space-y-4">
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <div className="space-y-2">
                    <p className="font-medium">✅ Distribuição já configurada!</p>
                    <p className="text-sm">
                      Este lote já possui {existingDistribution?.length || 0} distribuições configuradas.
                    </p>
                  </div>
                </AlertDescription>
              </Alert>

              {/* Tabela da distribuição existente */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Distribuição Atual ({existingDistribution?.length || 0} registros)
                </h3>
                <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                  <div className="max-h-60 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-700 font-medium">Boleto</th>
                          <th className="px-4 py-2 text-left text-gray-700 font-medium">Usuário</th>
                          <th className="px-4 py-2 text-left text-gray-700 font-medium">ID</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {existingDistribution?.slice(0, 20).map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-900">{row.nosso_numero}</td>
                            <td className="px-4 py-2 text-gray-900">{row.assignee_name}</td>
                            <td className="px-4 py-2 text-gray-900">{row.assignee_id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {existingDistribution && existingDistribution.length > 20 && (
                    <div className="px-4 py-2 bg-gray-100 text-gray-600 text-sm">
                      ... e mais {existingDistribution.length - 20} registros
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Alert className="bg-blue-50 border-blue-200">
              <FileSpreadsheet className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <div className="space-y-2">
                  <p className="font-medium">Formato esperado da planilha:</p>
                  <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                    <li><strong>boleto</strong>: Nosro número do boleto</li>
                    <li><strong>usuario</strong>: Nome do responsável</li>
                    <li><strong>id</strong>: ID do usuário no ChatConnect</li>
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Upload */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Selecione o arquivo Excel
            </label>
            <div className="flex items-center gap-3">
              <label
                htmlFor="file-upload"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <Upload className="h-6 w-6 text-gray-400" />
                <div className="text-center">
                  <p className="text-sm text-gray-700">
                    {file ? file.name : 'Clique para selecionar ou arraste o arquivo'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Formatos: .xlsx, .xls
                  </p>
                </div>
              </label>
              <input
                id="file-upload"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={loading}
                className="hidden"
              />
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center py-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
              <p className="text-sm text-gray-600 mt-2">Processando arquivo...</p>
            </div>
          )}

          {/* Erro */}
          {error && (
            <Alert className="bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Sucesso */}
          {success && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Distribuição salva com sucesso!
              </AlertDescription>
            </Alert>
          )}

          {/* Preview */}
          {preview && !loading && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-2xl font-bold text-gray-900">{preview.total}</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-green-600">Válidos</p>
                    <p className="text-2xl font-bold text-green-700">{preview.validos}</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-red-600">Inválidos</p>
                    <p className="text-2xl font-bold text-red-700">{preview.invalidos}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Amostra */}
              {preview.amostra.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Amostra dos dados (primeiras 10 linhas)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-700 font-medium">Boleto</th>
                          <th className="px-4 py-2 text-left text-gray-700 font-medium">Usuário</th>
                          <th className="px-4 py-2 text-left text-gray-700 font-medium">ID</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {preview.amostra.map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-900">{row.boleto}</td>
                            <td className="px-4 py-2 text-gray-900">{row.usuario}</td>
                            <td className="px-4 py-2 text-gray-900">{row.id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Erros */}
              {preview.erros.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-red-600 mb-2">
                    Erros encontrados ({preview.erros.length})
                  </h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-40 overflow-y-auto">
                    <ul className="text-xs text-red-800 space-y-1">
                      {preview.erros.map((erro, idx) => (
                        <li key={idx}>• {erro}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        <DialogFooter className="pt-6 gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            {hasExistingDistribution ? 'Fechar' : 'Cancelar'}
          </Button>
          {!hasExistingDistribution && (
            <Button
              onClick={handleSave}
              disabled={!preview || preview.validos === 0 || loading || success}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : success ? (
                'Salvo!'
              ) : (
                'Salvar Distribuição'
              )}
            </Button>
          )}
          {hasExistingDistribution && (
            <Button
              onClick={() => {
                setHasExistingDistribution(false);
                setExistingDistribution(null);
              }}
              className="w-full sm:w-auto bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
            >
              Substituir Distribuição
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDistribuicaoModal;

